import React, { FC, useEffect, useState, useMemo } from 'react';
import s from './AppointmentBookingSpec.module.scss';
import clsx from 'clsx';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';
import ChooseServices from './ChooseServices';
import { useTypedSelector } from 'store/useTypedSelector';
import BookingCheckoutMulti from './BookingCheckout/BookingCheckoutMulti';
import PaymentOption from './PaymentOption';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from 'sdk/api';
import useIsAuth from 'sdk/hooks/useIsAuth';
import ChooseLocation from 'components/shared/ChooseLocation';
import OrdersList from './OrdersList';
import Steps from './Steps';
import useGetServicesTeamsByTeamId from 'sdk/hooks/booking/useGetServicesTeamsByTeamId';
import useGetServicePricingDurationBySpec from 'sdk/hooks/booking/useGetServicePricingDurationBySpec';
import { useTranslation } from 'react-i18next';
import BookingCalendar from 'components/shared/BookingCalendar';

export enum AppointmentBookingSteps {
  CHOOSE_SERVICES = 'Choose services',
  CHOOSE_SERVICES_BOOK = 'Choose services book',
  SELECT_THE_PROFESSIONAL = 'Select the professional',
  CHOOSE_DATE = 'Choose date',
  CHOOSE_PAYMENT_OPTION = 'Choose payment option',
  CHECKOUT = 'Booking checkout',
  VOUCHER_EMAIL = 'Voucher email',
  VOUCHER_TIME = 'Voucher time',
  VOUCHER_RESULT = 'Voucher result',
  CHOOSE_LOCATION = 'Choose location',
  ORDERS_LIST = 'Orders list',
}

interface SelectedDay {
  year: number;
  month: number;
  day: number;
}

type Props = {};

const AppointmentBookingSpec: FC<Props> = (): JSX.Element => {
  const navigate = useNavigate();
  const { isAppointmentBookingOpen } = useTypedSelector(state => state.postSlice);
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { salonId, specialistId } = useParams();
  const { t } = useTranslation() as any;
  const { data: servicesTeams, refetch: refetchServicesTeams } = useGetServicesTeamsByTeamId(
    specialistId,
    selectedLocation,
  );

  const servicesIds = servicesTeams?.map(service => service?.services?.id);

  const { data: services, refetch: refetchServices } = useGetServicePricingDurationBySpec(
    salonId || '',
    selectedLocation || '',
    servicesIds || [],
  );

  const [selectedServices, setSelectedServices] = useState<any>([]);
  const [selectedProf, setSelectedProf] = useState(specialistId || null);
  const [currentServiceId, setCurrentServiceId] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [price, setPrice] = useState(0);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [selectedDay, setSelectedDay] = useState<SelectedDay | null>(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  const [homeLocation, setHomeLocation] = useState({
    street: '',
    apartment: '',
    floor: '',
    entrance: '',
    note: '',
    intercom: '',
    name: '',
    phone: '',
  });
  const [bookingItemsWithDiscount, setBookingItemsWithDiscount] = useState([]);
  const [bookingTransactions, setBookingTransactions] = useState<any>([]);
  const [selectedServiceHistory, setSelectedServiceHistory] = useState<any>([]);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);

  const [step, setStep] = useState(AppointmentBookingSteps.CHOOSE_LOCATION);
  const isChooseServices = AppointmentBookingSteps.CHOOSE_SERVICES;
  const isChooseDate = AppointmentBookingSteps.CHOOSE_DATE;
  const isChoosePaymentOption = AppointmentBookingSteps.CHOOSE_PAYMENT_OPTION;
  const isCheckout = AppointmentBookingSteps.CHECKOUT;
  const isChooseLocation = AppointmentBookingSteps.CHOOSE_LOCATION;
  const isOrdersList = AppointmentBookingSteps.ORDERS_LIST;

  const handleCloseMenu = () => {
    if (step === isChooseLocation) {
      navigate(-1);
      return;
    }
    if (step === isChooseServices) {
      setDuration(0);
      setPrice(0);
      setSelectedServiceHistory([]);
      setStep(isChooseLocation);
      return;
    }

    if (step === isChooseDate) {
      setSelectedTime(null);
      setSelectedEndTime(null);
      setSelectedDay(null);
      setStep(isChooseServices);
      if (selectedServices.length === 0) {
        setSelectedServices([selectedServiceHistory[selectedServiceHistory.length - 1]]);
      }
      return;
    }

    if (step === isChoosePaymentOption) {
      setPaymentMethod(null);
      setBookingTransactions(prev => {
        return prev.slice(0, -1);
      });
      setSelectedTime(null);
      setSelectedDay(null);
      setStep(isChooseDate);
      return;
    }

    if (step === isCheckout) {
      setStep(isChooseDate);
      return;
    }

    if (step === isOrdersList) {
      setStep(isChoosePaymentOption);
      return;
    }
  };

  const settingAppointments = () => {
    const isDuplicate = bookingTransactions.some(
      transaction =>
        transaction.team_id === specialistId &&
        (selectedDay
          ? transaction.date ===
            `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
              selectedDay.day,
            ).padStart(2, '0')}`
          : false) &&
        transaction.start_time === selectedTime &&
        transaction.service === currentServiceId,
    );

    if (isDuplicate) {
      return;
    }

    setBookingTransactions(prev => {
      return [
        ...prev,
        {
          order_id: 'Offline',
          team_id: specialistId,
          start_time: selectedTime,
          end_time: selectedEndTime,
          duration,
          date: selectedDay
            ? `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
                selectedDay.day,
              ).padStart(2, '0')}`
            : '',
          status: 'Booked',
          user_id: isAuthData?.user?.id,
          price: price,
          service: currentServiceId,
        },
      ];
    });
  };

  const handleDeleteBooking = index => {
    const newBookingTransactions = [...bookingTransactions];
    const newSelectedServiceHistory = [...selectedServiceHistory];

    newBookingTransactions.splice(index, 1);
    newSelectedServiceHistory.splice(index, 1);

    setBookingTransactions(newBookingTransactions);
    setSelectedServiceHistory(newSelectedServiceHistory);
  };

  useEffect(() => {
    refetchServices();
  }, [isAppointmentBookingOpen]);

  const preSelectedLocationType = useMemo(() => {
    if (!services || services.length === 0) return null;
    
    const locationTypes = services.map(service => service?.service_location).filter(Boolean);
    const uniqueLocationTypes = Array.from(new Set(locationTypes));
    
    if (uniqueLocationTypes.length === 1 && uniqueLocationTypes[0] !== 'Both') {
      return uniqueLocationTypes[0];
    }
    
    return null;
  }, [services]);

  return (
    <div className={clsx(s.appointment_booking)}>
      <ProfileMenuHeader text={'appointments_bookings'} onClose={handleCloseMenu} />
      {step !== isOrdersList && step !== isCheckout && <Steps step={step} />}
      {step === isChooseLocation && (
        <ChooseLocation
          handleNext={() => {
            refetchServicesTeams();
            setStep(AppointmentBookingSteps.CHOOSE_SERVICES);
          }}
          homeLocation={homeLocation}
          setHomeLocation={setHomeLocation}
          setSelectedLocation={setSelectedLocation}
          preSelectedLocationType={preSelectedLocationType}
        />
      )}
      {step === isChooseServices && (
        <ChooseServices
          services={services}
          selectedIds={selectedServices}
          handleChoose={setSelectedServices}
          setDuration={setDuration}
          setPrice={setPrice}
          handleNext={() => {
            const firstService = selectedServices[0];
            setCurrentServiceId(typeof firstService === 'object' ? firstService.id : firstService);
            setSelectedServiceHistory(selectedServices);
            setStep(AppointmentBookingSteps.CHOOSE_DATE);
          }}
          setSelectedServiceHistory={setSelectedServiceHistory}
          setBookingItemsWithDiscount={setBookingItemsWithDiscount}
        />
      )}
      {step === isChooseDate && (
        <BookingCalendar
          teamId={specialistId}
          salonId={salonId}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          setSelectedEndTime={setSelectedEndTime}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          handleNext={() => {
            settingAppointments();
            setStep(AppointmentBookingSteps.CHOOSE_PAYMENT_OPTION);
          }}
          duration={duration}
          selectedServices={selectedServices}
          selectedServiceHistory={selectedServiceHistory}
          currentServiceId={currentServiceId}
          alreadyBookedTimes={bookingTransactions}
        />
      )}
      {step === isOrdersList && (
        <OrdersList
          handleNext={() => setStep(AppointmentBookingSteps.CHECKOUT)}
          selectedServiceHistory={selectedServiceHistory}
          bookingTransactions={bookingTransactions}
          onDelete={handleDeleteBooking}
          price={price}
        />
      )}
      {step === isChoosePaymentOption && (
        <PaymentOption
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          serviceId={selectedServiceHistory}
          isLoading={isSubmittingBooking}
          handleNext={() => {
            const generatedNumber = Math.floor(100000 + Math.random() * 900000);
            if (paymentMethod === 'online') {
              setStep(AppointmentBookingSteps.ORDERS_LIST);
            } else {
              if (isSubmittingBooking) return;
              setIsSubmittingBooking(true);
              
              (async () => {
                const totalPrice = bookingTransactions.reduce(
                  (sum, order) => sum + (order.price || 0),
                  0,
                );
                const bookedTimes = bookingTransactions.reduce((acc, item) => {
                  acc[`teamId_${item.team_id}`] = {
                    start_time: item.start_time,
                    end_time: item.end_time,
                    duration: item.duration,
                    date: item.date,
                  };
                  return acc;
                }, {});
                const { data: transactions, error } = await supabase
                  .from('booking_transactions')
                  .insert({
                    team_id: specialistId,
                    date: selectedDay
                      ? `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
                          selectedDay.day,
                        ).padStart(2, '0')}`
                      : '',
                    duration,
                    order_id: 'Offline',
                    status: 'Booked',
                    user_id: userId,
                    price: totalPrice,
                    start_time: selectedTime,
                    end_time: selectedEndTime,
                    service: selectedServiceHistory[0],
                    service_ids: { serviceIds: selectedServiceHistory },
                    booked_times: bookedTimes,
                    home_address: homeLocation,
                    salon_id: salonId,
                    record_id: generatedNumber,
                  })
                  .select('*');

                if (error) {
                  setIsSubmittingBooking(false);
                  return;
                }

                try {
                  await supabase.functions.invoke('send-booking-email', {
                    body: { booking_transaction_id: transactions[0]?.id },
                  });
                } catch (emailError) {
                  console.error('Failed to send booking email:', emailError);
                }

                setIsSubmittingBooking(false);
                navigate(`/booking/${transactions[0]?.id}`);
              })();
            }
          }}
        />
      )}
      {step === isCheckout && (
        <BookingCheckoutMulti
          price={price}
          teamId={specialistId}
          bookingTransactions={bookingTransactions}
          duration={duration}
          selectedTime={selectedTime}
          selectedEndTime={selectedEndTime}
          date={
            selectedDay
              ? `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
                  selectedDay.day,
                ).padStart(2, '0')}`
              : ''
          }
          selectedServiceHistory={selectedServiceHistory}
          homeLocation={homeLocation}
          salonId={salonId}
          bookingItemsWithDiscount={bookingItemsWithDiscount}
          setBookingItemsWithDiscount={setBookingItemsWithDiscount}
        />
      )}
    </div>
  );
};

export default AppointmentBookingSpec;
